import { useState, useCallback } from 'react';
import type { Service as ServiceType, Barber as BarberType, Branch as BranchType } from '@/types';

// Allow simplified barber type for "any" option
export type BookingBarber = BarberType | { id: string; displayName: string };

export interface BookingState {
  currentStep: number;
  selectedBranch: BranchType | null;
  selectedService: ServiceType | null;
  selectedBarber: BarberType | null;
}

// Identity helpers tolerant to objects that only carry a generic `id`
function getServiceId(service: ServiceType | null): string | undefined {
  if (!service) return undefined;
  return service.serviceId ?? (service as { id?: string }).id;
}

function getBranchId(branch: BranchType | null): string | undefined {
  if (!branch) return undefined;
  return branch.branchId ?? (branch as { id?: string }).id;
}

export function useBooking() {
  const [bookingState, setBookingState] = useState<BookingState>({
    currentStep: 1,
    selectedBranch: null,
    selectedService: null,
    selectedBarber: null,
  });

  const selectBranch = useCallback((branch: BranchType) => {
    setBookingState((prev) => {
      // Re-selecting the same branch keeps the later selections (accordion "Cambiar")
      const isSameBranch =
        prev.selectedBranch !== null && getBranchId(prev.selectedBranch) === getBranchId(branch);
      const selectedService = isSameBranch ? prev.selectedService : null;
      const selectedBarber = isSameBranch ? prev.selectedBarber : null;

      return {
        selectedBranch: branch,
        // Clear subsequent selections when changing to a different branch
        selectedService,
        selectedBarber,
        // Jump to the first incomplete step
        currentStep: selectedService ? (selectedBarber ? 4 : 3) : 2,
      };
    });
  }, []);

  const selectService = useCallback((service: ServiceType) => {
    setBookingState((prev) => {
      // Re-selecting the same service keeps the barber (accordion "Cambiar")
      const isSameService =
        prev.selectedService !== null && getServiceId(prev.selectedService) === getServiceId(service);
      const selectedBarber = isSameService ? prev.selectedBarber : null;

      return {
        ...prev,
        selectedService: service,
        // Clear barber selection when changing to a different service
        selectedBarber,
        currentStep: selectedBarber ? 4 : 3,
      };
    });
  }, []);

  const selectBarber = useCallback((barber: BookingBarber) => {
    setBookingState((prev) => ({
      ...prev,
      selectedBarber: barber as BarberType,
      currentStep: 4, // Advance to confirmation
    }));
  }, []);

  const goBack = useCallback(() => {
    setBookingState((prev) => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1),
    }));
  }, []);

  // Expands a step without losing any selection (used by the accordion "Cambiar" buttons)
  const goToStep = useCallback((step: number) => {
    setBookingState((prev) => ({
      ...prev,
      currentStep: Math.max(1, Math.min(4, step)),
    }));
  }, []);

  const resetBooking = useCallback(() => {
    setBookingState({
      currentStep: 1,
      selectedBranch: null,
      selectedService: null,
      selectedBarber: null,
    });
  }, []);

  return {
    bookingState,
    selectBranch,
    selectService,
    selectBarber,
    goBack,
    goToStep,
    resetBooking,
  };
}
